/**
 * Feature Flag Toggle Component
 *
 * Displays a feature flag in a row format with:
 * - Toggle switch for enable/disable
 * - Name and description
 * - Scope badge
 * - Target count (plans/tenants)
 * - Action menu
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn, Card, CardContent, Button, Switch } from '@respectly/ui';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  Flag,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@respectly/ui';
import type { FeatureFlag } from '@respectly/shared-types';
import { FeatureFlagScopeBadge } from './FeatureFlagScopeBadge';

// ============================================
// Component Props
// ============================================

export interface FeatureFlagToggleProps {
  flag: FeatureFlag;
  isToggling?: boolean;
  onToggle?: (enabled: boolean) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

// ============================================
// Helper Functions
// ============================================

function getTargetCount(flag: FeatureFlag): { count: number; label: string } | null {
  if (flag.scope === 'global') return null;

  if (flag.scope === 'plan' && flag.targetPlans?.length) {
    return {
      count: flag.targetPlans.length,
      label: flag.targetPlans.length === 1 ? 'plan' : 'plans',
    };
  }

  if (flag.scope === 'tenant' && flag.targetTenants?.length) {
    return {
      count: flag.targetTenants.length,
      label: flag.targetTenants.length === 1 ? 'tenant' : 'tenants',
    };
  }

  return null;
}

function formatDate(date: Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================
// Component
// ============================================

export function FeatureFlagToggle({
  flag,
  isToggling,
  onToggle,
  onEdit,
  onDelete,
  className,
}: FeatureFlagToggleProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasActions = onEdit || onDelete;
  const targetInfo = getTargetCount(flag);

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all duration-200',
        'border-slate-200/60 dark:border-slate-700/60',
        'hover:border-slate-300 dark:hover:border-slate-600',
        className
      )}
    >
      {/* Status indicator */}
      <div
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1',
          flag.enabled
            ? 'bg-emerald-500'
            : 'bg-slate-300 dark:bg-slate-600'
        )}
      />

      <CardContent className="p-4 pl-5">
        <div className="flex items-start gap-4">
          {/* Toggle */}
          <div className="pt-0.5">
            <Switch
              checked={flag.enabled}
              onCheckedChange={onToggle}
              disabled={isToggling}
              className={cn(isToggling && 'opacity-50')}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {/* Name and key */}
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    {flag.name}
                  </h3>
                  <code className="px-1.5 py-0.5 text-xs font-mono bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                    {flag.key}
                  </code>
                </div>

                {/* Description */}
                {flag.description && (
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
                    {flag.description}
                  </p>
                )}

                {/* Badges */}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <FeatureFlagScopeBadge scope={flag.scope} size="sm" />

                  {targetInfo && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                      {targetInfo.count} {targetInfo.label}
                    </span>
                  )}

                  {/* Expand button for targets */}
                  {(flag.targetPlans?.length || flag.targetTenants?.length) && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-3 h-3" />
                          Hide
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" />
                          Show
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Updated date */}
                <span className="hidden sm:block text-xs text-slate-400 dark:text-slate-500">
                  Updated {formatDate(flag.updatedAt)}
                </span>

                {hasActions && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      {onEdit && (
                        <DropdownMenuItem onClick={onEdit}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={onDelete}
                            className="text-red-600 dark:text-red-400"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {/* Expanded targets */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                    {flag.targetPlans?.length ? (
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                          Target Plans:
                        </span>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {flag.targetPlans.map((planId) => (
                            <span
                              key={planId}
                              className="px-2 py-0.5 text-xs font-mono bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded"
                            >
                              {planId}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {flag.targetTenants?.length ? (
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                          Target Tenants:
                        </span>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {flag.targetTenants.map((tenantId) => (
                            <span
                              key={tenantId}
                              className="px-2 py-0.5 text-xs font-mono bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded"
                            >
                              {tenantId}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton Component
// ============================================

export function FeatureFlagToggleSkeleton({ className }: { className?: string }) {
  return (
    <Card
      className={cn(
        'border-slate-200/60 dark:border-slate-700/60',
        className
      )}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-200 dark:bg-slate-700" />
      <CardContent className="p-4 pl-5">
        <div className="flex items-start gap-4 animate-pulse">
          <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 rounded-full" />
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
            <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="flex items-center gap-2">
              <div className="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
              <div className="h-6 w-20 bg-slate-200 dark:bg-slate-700 rounded-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// List Component
// ============================================

export function FeatureFlagList({
  flags,
  togglingId,
  onToggle,
  onEdit,
  onDelete,
  className,
}: {
  flags: FeatureFlag[];
  togglingId?: string | null;
  onToggle?: (flag: FeatureFlag, enabled: boolean) => void;
  onEdit?: (flag: FeatureFlag) => void;
  onDelete?: (flag: FeatureFlag) => void;
  className?: string;
}) {
  if (flags.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <Flag className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
          No feature flags found
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Create your first feature flag to control feature rollout.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {flags.map((flag, index) => (
        <motion.div
          key={flag.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: index * 0.05 }}
        >
          <FeatureFlagToggle
            flag={flag}
            isToggling={togglingId === flag.id}
            onToggle={onToggle ? (enabled) => onToggle(flag, enabled) : undefined}
            onEdit={onEdit ? () => onEdit(flag) : undefined}
            onDelete={onDelete ? () => onDelete(flag) : undefined}
          />
        </motion.div>
      ))}
    </div>
  );
}
